'use client';

import { cn } from '@/lib/utils';
import SectionHeading from '../layout/section-heading';

/**
 * ✅ This version adds an image to EACH card.
 * Layout:
 * - Desktop: Image on left + content on right (alternates for variety)
 * - Mobile: Image on top + content below
 *
 * Replace the `image` paths with your real assets.
 */

const steps = [
    {
        no: '01',
        title: 'Every Story Starts with a Dream',
        desc: `Children connect with stories that feel warm, friendly, and imaginative. Our publishing services help authors build books that reflect their ideas with charm and clarity. With our support, your story becomes a delightful reading experience for little ones.`,
        image: '/child-ebook/Step-04.png',
    },
    {
        no: '02',
        title: 'Turn Ideas into Colorful Worlds',
        desc: `Kids love books filled with lively characters and bright moments. We help transform your concept into a polished, well-designed children’s book that stands out on any bookshelf. Our organized self-publishing workflow ensures your imagination becomes a playful world that young readers can explore.`,
        image: '/child-ebook/Step-02.png',
    },
    {
        no: '03',
        title: 'Celebrate Creativity on Every Page',
        desc: `A children’s book should feel joyful from start to finish. Our team ensures your story is presented in a way that captures attention and encourages young minds to dream big. Through thoughtful editing and design within our self-publishing system, your book becomes a source of happiness.`,
        image: '/child-ebook/Step-03.png',
    },
    {
        no: '04',
        title: 'Bring Stories to Life for Kids Everywhere',
        desc: `We work closely with authors to develop books that connect with children of all ages. Whether your story is sweet, adventurous, or educational, our publishing team helps you create something truly special and memorable.`,
        image: '/child-ebook/Step-01.png',
    },
    {
        no: '05',
        title: 'Create Lasting Childhood Memories',
        desc: `Children often remember the books they grew up with. We help you craft meaningful stories that kids will read, reread, and hold onto as they grow. Your book could become a treasured part of someone’s childhood.`,
        image: '/child-ebook/Step-05.png',
    },
];

// Optional: decorative background icons for the whole section
const bgIcons = [
    { src: '/child-icons/star.svg', className: 'top-10 right-12 w-10 rotate-[10deg]' },
    { src: '/child-icons/cloud.svg', className: 'bottom-10 left-12 w-12 rotate-[-8deg]' },
    { src: '/child-icons/pencil.svg', className: 'top-16 left-8 w-10 rotate-[-12deg]' },
];

function StepCardWithImage({ no, title, desc, image, index }) {
    const reverse = index % 2 === 1;

    const accents = [
        'from-pink-500/20 via-purple-500/10 to-blue-500/15',
        'from-amber-500/20 via-orange-500/10 to-red-500/15',
        'from-emerald-500/20 via-teal-500/10 to-cyan-500/15',
        'from-blue-500/20 via-indigo-500/10 to-purple-500/15',
        'from-fuchsia-500/20 via-pink-500/10 to-rose-500/15',
    ];

    return (
        <div
            className={cn(
                'group relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all duration-300',
                'hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-950/40'
            )}
        >
            {/* Accent wash */}
            <div
                className={cn(
                    'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-70',
                    accents[index % accents.length]
                )}
            />

            {/* Layout */}
            <div
                className={cn(
                    'relative z-10 grid grid-cols-1 gap-0 lg:grid-cols-12',
                    reverse && 'lg:[direction:rtl]'
                )}
            >
                {/* Image */}
                <div className={cn('relative lg:col-span-5', reverse && 'lg:[direction:ltr]')}>
                    <div className="relative h-56 w-full lg:h-full">
                        <img
                            src={image}
                            alt={title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        {/* Image overlay gradient for consistency */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />

                        {/* Number badge */}
                        <div className="absolute left-4 top-4">
                            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary text-white font-bold shadow-lg">
                                {no}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div
                    className={cn(
                        'p-6 lg:col-span-7 lg:p-8 flex flex-col justify-center',
                        reverse && 'lg:[direction:ltr]'
                    )}
                >
                    <h3 className="fs-36 font-semibold text-slate-900 dark:text-white">{title}</h3>
                    <p className="mt-3 leading-relaxed text-slate-600 dark:text-slate-300">
                        {desc}
                    </p>

                    {/* Small “sparkle” hover highlight */}
                    <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/40 blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:bg-white/10" />
                </div>
            </div>
        </div>
    );
}

export default function ChildPublishingStepsWithImagesSection({
    className,
    title = 'Create Meaningful Books for Young Readers with Complete Self-Publishing Support.',
    description = '',
}) {
    return (
        <section className={cn('py-16 lg:py-20', className)}>
            <div className="container mx-auto">
                {/* Header block with decorative icons */}
                <div>
                    <div className="relative z-10">
                        <SectionHeading
                            titleClassName="mx-auto mb-4 max-w-5xl fs-44"
                            descriptionClassName="max-w-3xl mx-auto"
                            align="center"
                            title={title}
                            description={description}
                        />
                    </div>
                </div>

                {/* Cards */}
                <div className="mt-10 flex flex-col gap-6 max-w-6xl mx-auto">
                    {steps.map((s, idx) => (
                        <StepCardWithImage key={s.no} {...s} index={idx} />
                    ))}
                </div>
            </div>
        </section>
    );
}
